import React, { useState } from 'react';
import { Plus, Trash, Edit3, Settings, Check, FileText, Cpu, Play } from 'react-feather';

const DragHandleIcon = () => (
  <svg 
    width="12" 
    height="12" 
    viewBox="0 0 12 12" 
    style={{ 
      cursor: 'grab', 
      marginRight: '6px', 
      opacity: 0.5, 
      color: 'var(--text-muted)',
      flexShrink: 0
    }}
  >
    <circle cx="3" cy="3" r="1.2" fill="currentColor" />
    <circle cx="3" cy="6" r="1.2" fill="currentColor" />
    <circle cx="3" cy="9" r="1.2" fill="currentColor" />
    <circle cx="7" cy="3" r="1.2" fill="currentColor" />
    <circle cx="7" cy="6" r="1.2" fill="currentColor" />
    <circle cx="7" cy="9" r="1.2" fill="currentColor" />
  </svg>
);

const FileExplorer = ({ 
  files, 
  activeFileId, 
  onSelectFile, 
  onAddFile, 
  onDeleteFile, 
  onRenameFile, 
  topModule, 
  setTopModule,
  testbenchModule,
  setTestbenchModule,
  onReorderFiles
}) => {
  const [renamingId, setRenamingId] = useState(null);
  const [renameDraft, setRenameDraft] = useState('');
  const [activeSettingsId, setActiveSettingsId] = useState(null);
  const [draggedIndex, setDraggedIndex] = useState(null);

  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', index);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
  };

  const handleDrop = (e, index) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;
    
    const reordered = [...files];
    const [removed] = reordered.splice(draggedIndex, 1);
    reordered.splice(index, 0, removed);
    
    if (onReorderFiles) {
      onReorderFiles(reordered);
    }
    setDraggedIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  const startRename = (e, file) => {
    e.stopPropagation();
    setRenamingId(file.id);
    setRenameDraft(file.name);
    setActiveSettingsId(null);
  };

  const handleRenameSubmit = (fileId) => {
    if (renameDraft.trim()) {
      onRenameFile(fileId, renameDraft.trim());
    }
    setRenamingId(null);
    setRenameDraft('');
  };

  const handleRenameKeyDown = (e, fileId) => {
    if (e.key === 'Enter') {
      handleRenameSubmit(fileId);
    } else if (e.key === 'Escape') {
      setRenamingId(null);
    }
  };

  const toggleSettings = (e, fileId) => {
    e.stopPropagation();
    if (activeSettingsId === fileId) {
      setActiveSettingsId(null);
    } else {
      setActiveSettingsId(fileId);
    }
  };

  const selectTopModule = (e, fileName) => {
    e.stopPropagation();
    const cleanName = fileName.split('.')[0];
    setTopModule(cleanName);
    setActiveSettingsId(null);
  };

  const selectTestbenchModule = (e, fileName) => {
    e.stopPropagation();
    const cleanName = fileName.split('.')[0];
    setTestbenchModule(cleanName);
    setActiveSettingsId(null);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', backgroundColor: 'var(--sidebar-bg)', color: 'var(--text-muted)', borderRight: '1px solid var(--border-color)', fontSize: '13px' }}>
      
      {/* File Explorer Header */}
      <div style={{ padding: '10px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid var(--border-color)', background: 'var(--tab-inactive-bg)' }}>
        <span style={{ fontWeight: 'bold', fontSize: '11px', letterSpacing: '1px', color: 'var(--text-muted)' }}>WORKSPACE FILES</span>
        <button 
          onClick={onAddFile}
          title="Create New File"
          style={{ background: 'transparent', border: 'none', color: '#3b82f6', cursor: 'pointer', display: 'flex', alignItems: 'center', padding: '2px' }}
        >
          <Plus size={16} />
        </button>
      </div>

      {/* Directory structure list */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '8px 0' }}>
        
        {/* Project root folder directory line */}
        <div style={{ padding: '6px 16px', fontWeight: 'bold', fontSize: '12px', color: 'var(--text-main)', display: 'flex', alignItems: 'center', gap: '6px' }}>
          <span>📁 src</span>
        </div>

        {/* Individual File rows */}
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          {files.map((file, index) => {
            const isActive = file.id === activeFileId;
            const isRenaming = file.id === renamingId;
            const cleanName = file.name.split('.')[0];
            const isTop = topModule === cleanName;
            const isTB = testbenchModule === cleanName;

            return (
              <div 
                key={file.id}
                onClick={() => !isRenaming && onSelectFile(file.id)}
                draggable={!isRenaming}
                onDragStart={(e) => handleDragStart(e, index)}
                onDragOver={(e) => handleDragOver(e, index)}
                onDrop={(e) => handleDrop(e, index)}
                onDragEnd={handleDragEnd}
                style={{ 
                  padding: '6px 16px 6px 12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  backgroundColor: isActive ? 'var(--tab-active-bg)' : 'transparent',
                  opacity: draggedIndex === index ? 0.4 : 1,
                  cursor: 'pointer',
                  position: 'relative',
                  borderLeft: isActive ? '2px solid #3b82f6' : '2px solid transparent',
                  transition: 'background-color 0.1s, opacity 0.1s'
                }}
              >
                {/* File Details / Input Rename controls */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
                  {!isRenaming && <DragHandleIcon />}
                  <FileText size={14} color={file.name.endsWith('.sv') ? '#8b5cf6' : file.name.endsWith('.sdc') ? '#f59e0b' : '#3b82f6'} />
                  
                  {isRenaming ? (
                    <input
                      autoFocus
                      type="text"
                      value={renameDraft}
                      onChange={(e) => setRenameDraft(e.target.value)}
                      onBlur={() => handleRenameSubmit(file.id)}
                      onKeyDown={(e) => handleRenameKeyDown(e, file.id)}
                      onClick={(e) => e.stopPropagation()}
                      style={{ 
                        background: 'var(--editor-bg)', 
                        border: '1px solid #3b82f6', 
                        borderRadius: '3px', 
                        color: 'var(--text-main)', 
                        fontSize: '12px', 
                        padding: '1px 6px',
                        width: '120px',
                        outline: 'none'
                      }}
                    />
                  ) : (
                    <span style={{ color: isActive ? 'var(--text-main)' : 'var(--text-muted)', fontWeight: isActive ? '600' : 'normal', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {file.name}
                    </span>
                  )}

                  {/* Top / Testbench target flag tags */}
                  {isTop && (
                    <span title="Design Top Module" style={{ display: 'inline-flex', alignItems: 'center', color: '#3b82f6' }}>
                      <Cpu size={11} fill="rgba(59,130,246,0.1)" />
                    </span>
                  )}
                  {isTB && (
                    <span title="Simulation Testbench" style={{ display: 'inline-flex', alignItems: 'center', color: '#10b981' }}>
                      <Play size={11} fill="rgba(16,185,129,0.1)" />
                    </span>
                  )}
                </div>

                {/* Operations / Actions Settings Cogs */}
                {!isRenaming && (
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <button 
                      onClick={(e) => toggleSettings(e, file.id)}
                      title="File Targets Setting"
                      style={{ background: 'transparent', border: 'none', color: '#475569', cursor: 'pointer', padding: '2px', display: 'flex', alignItems: 'center' }}
                    >
                      <Settings size={12} />
                    </button>
                    <button 
                      onClick={(e) => { e.stopPropagation(); onDeleteFile(file.id); }}
                      title="Delete File"
                      style={{ background: 'transparent', border: 'none', color: '#475569', cursor: 'pointer', padding: '2px', display: 'flex', alignItems: 'center' }}
                    >
                      <Trash size={12} />
                    </button>
                  </div>
                )}

                {/* Floating Context Targets Menu */}
                {activeSettingsId === file.id && (
                  <div style={{ 
                    position: 'absolute', 
                    top: '26px', 
                    right: '16px', 
                    background: '#0f172a', 
                    border: '1px solid #1e293b', 
                    borderRadius: '4px', 
                    zIndex: 20, 
                    boxShadow: '0 4px 12px rgba(0,0,0,0.5)',
                    padding: '4px 0',
                    fontSize: '11px',
                    width: '150px'
                  }}>
                    <div 
                      onClick={(e) => selectTopModule(e, file.name)}
                      style={{ padding: '6px 12px', color: '#cbd5e1', display: 'flex', alignItems: 'center', gap: '6px' }}
                    >
                      {isTop ? <Check size={12} color="#3b82f6" /> : <span style={{ width: '12px' }}></span>}
                      Set as Design Top
                    </div>
                    <div 
                      onClick={(e) => selectTestbenchModule(e, file.name)}
                      style={{ padding: '6px 12px', color: '#cbd5e1', display: 'flex', alignItems: 'center', gap: '6px' }}
                    >
                      {isTB ? <Check size={12} color="#10b981" /> : <span style={{ width: '12px' }}></span>}
                      Set as Testbench
                    </div>
                    <div style={{ borderTop: '1px solid #1e293b', margin: '4px 0' }}></div>
                    <div 
                      onClick={(e) => startRename(e, file)}
                      style={{ padding: '6px 12px', color: '#cbd5e1', display: 'flex', alignItems: 'center', gap: '6px' }}
                    >
                      <Edit3 size={12} />
                      Rename File
                    </div>
                  </div>
                )}

              </div>
            );
          })}
        </div>

      </div>
    </div>
  );
};

export default FileExplorer;
